import { useApp } from "@hooks/useApp";
import { useCore } from "@hooks/useCore";
import { useKlaviyo } from "@hooks/useKlaviyo";
import { useMounted } from "@hooks/useMounted";
import { useProductContext } from "@hooks/useProduct";
import { useRoutes } from "@hooks/useRoutes";
import { useSale } from "@hooks/useSale";
import { type DropdownOption } from "@hooks/useSelector";
import { useSettings } from "@hooks/useSettings";
import { useShopify } from "@hooks/useShopify";
import { useTemplate } from "@hooks/useTemplate";
import { useWindowSize } from "@hooks/useWindowSize";
import { HistoryLocation } from "@reach/router";
import {
  type AsGraph,
  type Badge,
  type ComponentProps,
  type GatsbyImageSourceProps,
  type NormalisedProduct,
  type NormalisedProductVariant,
} from "@ts/components";
import {
  Sections,
  type AdditionalContent,
  type Collection,
  type ProductVideo,
  type SanityKeyed,
  type Product as SanityProduct,
  type TemplateProduct,
} from "@ts/sanity";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type LegacyRef,
  type MutableRefObject,
  type Ref,
} from "react";
import SwiperCore from "swiper";

export type ProductInputProps = ComponentProps & {
  content: AsGraph<SanityProduct>;
  location: HistoryLocation;
  template: TemplateProduct;
  imageGallery?: React.ReactNode;
  howToStyleSection?: React.ReactNode;
  asSeenOnSection?: React.ReactNode;
  featureAndShoeCareSheet?: React.ReactNode;
  productName: string;
  productGender: "Mens" | "Womens";
  productStatus: "Core" | "Seasonal" | string;
  productRecommendations?: React.ReactNode;
  productUpsells?: React.ReactNode;
  primaryCollection?: {
    handle: string;
    title: string;
  };
  swatches?: React.ReactNode;
  seoFeatureDescription?: React.ReactNode;
  siblingColours?: string[];
  reviewsSummary?: React.ReactNode;
  reviewNugget?: React.ReactNode;
  children: React.ReactNode;
};

export type ProductOutputProps = {
  additionalContents: SanityKeyed<AdditionalContent>[];
  addToCartRef: MutableRefObject<HTMLDivElement | undefined>;
  badge?: Badge;
  content: { content: Sections | undefined };
  productSpecificContent: { content: Sections | undefined };
  contentByTag: any;
  description: string;
  displayFooter: boolean;
  displaySelector: boolean;
  dropdownOptions: DropdownOption[];
  findInStoreEnabled?: boolean;
  flashSaleDescription: string;
  flashSaleTitle: string;
  handle: string;
  handleChange: (value: string) => void;
  handleDropdownClose: () => void;
  handleSlideChange: (swiper: SwiperCore) => void;
  heroImage: GatsbyImageSourceProps;
  images: GatsbyImageSourceProps[];
  isAccessory: boolean;
  isFlashSaleProduct: boolean;
  onSale: boolean;
  parentCollection: Collection;
  paths: {
    title: string;
    url?: string;
  }[];
  preorderMessage: string;
  product: NormalisedProduct;
  productGroup?: string;
  saleTag?: string;
  sectionsRef: LegacyRef<HTMLDivElement>;
  selectedValue: string;
  selectedVariant: NormalisedProductVariant;
  selectedVariantAccessories: NormalisedProductVariant[];
  selectorRef: Ref<{ state: { menuIsOpen: boolean } }>;
  sku?: string;
  slideIndex: number;
  subTitle?: string;
  tags: string[];
  video: ProductVideo;
  outOfStockMessage: JSX.Element;
  imageGallery?: React.ReactNode;
  howToStyleSection?: React.ReactNode;
  asSeenOnSection?: React.ReactNode;
  featureAndShoeCareSheet?: React.ReactNode;
  productName: string;
  productGender: "Mens" | "Womens";
  productStatus: "Core" | "Seasonal" | string;
  productRecommendations?: React.ReactNode;
  productUpsells?: React.ReactNode;
  swatches?: React.ReactNode;
  seoFeatureDescription?: React.ReactNode;
  primaryCollection?: {
    handle: string;
    title: string;
  };
  siblingColours?: string[];
  reviewsSummary?: React.ReactNode;
  reviewNugget?: React.ReactNode;
  children: React.ReactNode;
};

export const withProduct =
  (Component: FC<ProductOutputProps>) =>
  ({
    name = "Product",
    content,
    template,
    location,
    imageGallery,
    howToStyleSection,
    asSeenOnSection,
    productName,
    productGender,
    productStatus,
    productRecommendations,
    productUpsells,
    primaryCollection,
    swatches,
    seoFeatureDescription,
    siblingColours,
    reviewsSummary,
    reviewNugget,
    featureAndShoeCareSheet,
    children,
  }: ProductInputProps) => {
    const {
      product,
      loading,
      selectedVariant,
      selectedVariantAccessories,
      dropdownOptions,
      selectedValue,
      handleOptionChange,
    } = useProductContext();
    const {
      tags,
      handle,
      image,
      images: _images,
      variants,
      metafields,
    } = product;
    const {
      product: { mensFootbedImage, womensFootbedImage },
    } = useTemplate();
    const hideFootbed = metafields?.find(
      (metafield) => metafield?.key === "hide_footbed_image",
    )?.value;

    const launchDateMetafield = metafields?.find(
      (metafield) => metafield?.key === "launch_date",
    )?.value;

    const footbedImage = !hideFootbed
      ? productGender === "Womens"
        ? womensFootbedImage?.asset
        : mensFootbedImage?.asset
      : null;
    const images = useMemo(() => {
      if (!footbedImage) return _images;

      const startImages = Array.isArray(_images) ? _images.slice(0, 2) : [];
      const endImages = Array.isArray(_images) ? _images.slice(2) : [];

      return [...startImages, footbedImage, ...endImages];
    }, [_images, footbedImage]);

    const {
      helpers: { storage, isBrowser },
    } = useCore();
    const {
      config: {
        settings: { keys },
      },
      setAddToCartError,
      globalStateReducer,
    } = useApp();
    const { routeResolver } = useRoutes();
    const {
      windowSize: { isMD },
    } = useWindowSize();
    const { formatProductGroup, getSaleTagFromTags, getBadgeFromTags } =
      useShopify();
    const { isSaleActive } = useSale();
    const { trackProduct } = useKlaviyo();
    const { hasMounted } = useMounted();
    const { findInStore, flashSale } = useSettings();
    const addToCartRef = useRef<HTMLDivElement | undefined>();
    const sectionsRef = useRef<HTMLDivElement | undefined>();
    const selectorRef = useRef<{ state: { menuIsOpen: boolean } }>();
    const [isKlaviyoEventDispatched, setIsKlaviyoEventDispatched] =
      useState(false);
    const [displayFooter, setDisplayFooter] = useState(false);
    const [chatButtonNode, setChatButtonNode] = useState(undefined);
    const [slideIndex, setSlideIndex] = useState(0);
    const [, dispatch] = globalStateReducer;
    const { video, content: flexibleSections } =
      content?.edges?.[0]?.node || {};
    const { additionalFlexibleSections, additionalSections } = template || {};
    const {
      title: flashSaleTitle,
      description: flashSaleDescription,
      enabled: flashSaleEnabled,
      slug: flashSaleSlug,
    } = flashSale;

    const saleTag = getSaleTagFromTags(tags);
    const badge = product.availableForSale
      ? getBadgeFromTags(tags, undefined, undefined, launchDateMetafield)
      : ({ name: "Out Of Stock", colour: "dark" } satisfies Badge);
    const isAccessory = !!tags?.find((tag) => tag === "type:accessory");
    const isFlashSaleProduct =
      flashSaleEnabled &&
      tags?.some(
        (tag) => tag?.toLowerCase() === `flash:${flashSaleSlug?.current}`,
      );
    const productGroup = formatProductGroup(tags);
    const subTitle = product?.title?.replace(productName, ``);
    const sku = (selectedVariant || variants[0])?.sku;

    const filteredContent = additionalSections?.filter((section) =>
      section?.tags?.some((tag) => tags?.includes(tag)),
    );
    const productSpecificContent = { content: flexibleSections };
    const templateContent = { content: additionalFlexibleSections };
    const contentByTag = { content: filteredContent?.[0]?.sections };
    const displaySelector = variants?.length > 1;
    const findInStoreEnabled = findInStore?.enabled;
    const onSale = !!saleTag && isSaleActive();

    const originalCollection = useMemo(() => {
      const lastCollection = location?.state?.collection;
      if (!lastCollection) return primaryCollection;
      return {
        shopify: {
          shopifyHandle: lastCollection.handle,
        },
        title: lastCollection.title,
      };
    }, [location, primaryCollection]);

    const parentCollection = useMemo(() => {
      if (!location?.state?.collection && !originalCollection) return null;
      return location?.state?.collection
        ? location?.state?.collection
        : originalCollection;
    }, [location, originalCollection]);

    const savedFilters = storage.get(keys.collection_filters);
    const path = parentCollection
      ? routeResolver({ item: parentCollection, type: `collection` })
      : null;
    const paths = useMemo<ProductOutputProps["paths"]>(() => {
      const isGiftCard = product?.tags?.includes("type:giftcard");

      if (isGiftCard) {
        return [];
      }

      return [
        {
          title: productGender === "Womens" ? "Women's" : "Men's",
          url: productGender === "Womens" ? "/women" : "/men",
        },
        {
          title: parentCollection?.title,
          url: `${path}${savedFilters && savedFilters.path === path ? `?${savedFilters.search}` : ""}`,
        },
      ];
    }, [parentCollection, path, savedFilters, product?.tags, productGender]);

    const outOfStockMessage = React.useMemo(() => {
      const templateTextWithCollection =
        template.additionalOutOfStockMessageWithCollection ||
        "Oh no, you've missed out! This is out of stock. Check out our ${collection} for more.";

      const templateTextWithoutCollection =
        template.additionalOutOfStockMessageWithoutCollection ||
        "Oh no, you've missed out! This is out of stock.";

      const _parentCollection = parentCollection
        ? {
            ...parentCollection,
            href: routeResolver({ item: parentCollection, type: `collection` }),
          }
        : null;

      const templateHtml = _parentCollection
        ? `<p>${templateTextWithCollection.replace("${collection}", `<a href="${_parentCollection.href}">${_parentCollection.title}</a>`)}</p>`
        : `<p>${templateTextWithoutCollection}</p>`;

      return <div dangerouslySetInnerHTML={{ __html: templateHtml }} />;
    }, [parentCollection, template]);

    const handleSlideChange = (index: number) => {
      setSlideIndex(index);
    };

    const isInView = (el: HTMLElement) => {
      return el.getBoundingClientRect().top >= 90;
    };

    const handleDropdownClose = () => {
      const hidden = addToCartRef?.current && isInView(addToCartRef.current);
      setDisplayFooter((prev) => (prev !== !hidden ? !hidden : prev));
    };

    const trackScrolling = () => {
      const hidden =
        addToCartRef?.current &&
        isInView(addToCartRef.current) &&
        !selectorRef?.current?.state?.menuIsOpen;
      setDisplayFooter((prev) => (prev !== !hidden ? !hidden : prev));
    };

    useEffect(() => {
      if (isMD) {
        return;
      }

      document.addEventListener("scroll", trackScrolling);

      return () => {
        document.removeEventListener("scroll", trackScrolling);
      };
    }, [isMD]);

    useEffect(() => {
      if (hasMounted && !loading && !isKlaviyoEventDispatched) {
        trackProduct(product);
        setIsKlaviyoEventDispatched(true);
      }
    }, [hasMounted, loading, isKlaviyoEventDispatched]);

    useEffect(() => {
      const cssUpdate = (element, style) => {
        for (const property in style) element.style[property] = style[property];
      };

      if (chatButtonNode) {
        cssUpdate(chatButtonNode, {
          marginTop: displayFooter && !isMD ? "-90px" : "-30px",
        });
      }

      return () => {
        if (chatButtonNode) {
          cssUpdate(chatButtonNode, {
            marginTop: "-30px",
          });
        }
      };
    }, [chatButtonNode, displayFooter]);

    useEffect(() => {
      let selfButtonDetect = null;

      const clearButtonDetector = () => {
        if (selfButtonDetect) {
          clearTimeout(selfButtonDetect);
        }
      };

      const getChatButtonElementNode = () => {
        const node =
          document.getElementById("inside_tabs") ||
          document.getElementById("live-chat-container");
        if (node) {
          setChatButtonNode(node);
          clearButtonDetector();
        } else {
          selfButtonDetect = setTimeout(getChatButtonElementNode, 3000);
        }
      };

      if (isBrowser) {
        getChatButtonElementNode();
      }

      return () => {
        clearButtonDetector();
      };
    }, [isBrowser]);

    useEffect(() => {
      if (selectedVariant) {
        setAddToCartError(false);

        dispatch({
          type: "setSelectedVariant",
          selectedVariant,
        });
      }
    }, [selectedVariant]);

    Component.displayName = name;
    return (
      <Component
        addToCartRef={addToCartRef}
        badge={badge}
        content={templateContent}
        contentByTag={contentByTag}
        displayFooter={displayFooter}
        displaySelector={displaySelector}
        dropdownOptions={dropdownOptions}
        findInStoreEnabled={findInStoreEnabled}
        flashSaleDescription={flashSaleDescription}
        flashSaleTitle={flashSaleTitle}
        handle={handle}
        handleChange={handleOptionChange}
        handleDropdownClose={handleDropdownClose}
        handleSlideChange={handleSlideChange}
        heroImage={image}
        images={images}
        isAccessory={isAccessory}
        isFlashSaleProduct={isFlashSaleProduct}
        onSale={onSale}
        parentCollection={parentCollection}
        primaryCollection={primaryCollection}
        productSpecificContent={productSpecificContent}
        paths={paths}
        preorderMessage={product?.preorderMessage || ""}
        productName={productName}
        productGender={productGender}
        productStatus={productStatus}
        productGroup={productGroup}
        product={product}
        saleTag={saleTag}
        selectedValue={selectedValue}
        sectionsRef={sectionsRef}
        selectedVariant={selectedVariant}
        selectedVariantAccessories={selectedVariantAccessories}
        selectorRef={selectorRef}
        slideIndex={slideIndex}
        sku={sku}
        subTitle={subTitle}
        video={video}
        outOfStockMessage={outOfStockMessage}
        imageGallery={imageGallery}
        howToStyleSection={howToStyleSection}
        asSeenOnSection={asSeenOnSection}
        productRecommendations={productRecommendations}
        productUpsells={productUpsells}
        swatches={swatches}
        seoFeatureDescription={seoFeatureDescription}
        siblingColours={siblingColours}
        reviewsSummary={reviewsSummary}
        reviewNugget={reviewNugget}
        featureAndShoeCareSheet={featureAndShoeCareSheet}
      >
        {children}
      </Component>
    );
  };
